import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from './api';

interface User {
    id: string;
    email: string;
    full_name: string;
    role: string;
    language: string;
    tenant_id: string;
    tenant_name?: string;
}

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isRevalidating: boolean;
    setAuth: (user: User) => void;
    logout: () => void;
    clearLocalAuth: () => void;
    updateUser: (user: Partial<User>) => void;
    revalidateSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            isRevalidating: false,
            setAuth: (user) => {
                set({ user, isAuthenticated: true, isRevalidating: false });
            },
            logout: () => {
                // Clear httpOnly cookies via server endpoint (fire-and-forget)
                authApi.logout().catch(() => {});
                set({ user: null, isAuthenticated: false, isRevalidating: false });
            },
            clearLocalAuth: () => {
                // Clear local state only — do NOT call server logout
                // Used by 401 interceptor when token is already invalid
                set({ user: null, isAuthenticated: false, isRevalidating: false });
            },
            updateUser: (userData) =>
                set((state) => ({
                    user: state.user ? { ...state.user, ...userData } : null,
                })),
            revalidateSession: async () => {
                const state = get();
                // Only revalidate if we have a persisted user
                if (!state.user) {
                    set({ isAuthenticated: false, isRevalidating: false });
                    return;
                }

                set({ isRevalidating: true });
                try {
                    const me = await authApi.getMe();
                    set({
                        user: {
                            id: me.id,
                            email: me.email,
                            full_name: me.full_name,
                            role: me.role,
                            language: me.language,
                            tenant_id: me.tenant_id,
                            tenant_name: me.tenant_name,
                        },
                        isAuthenticated: true,
                        isRevalidating: false,
                    });
                } catch {
                    // Session invalid — clear local state silently
                    set({ user: null, isAuthenticated: false, isRevalidating: false });
                }
            },
        }),
        {
            name: 'auth-storage',
            // Only persist user info — token is in httpOnly cookie (not accessible to JS)
            partialize: (state) => ({
                user: state.user,
                // Do NOT persist isAuthenticated — it will be verified on rehydration
            }),
            onRehydrateStorage: () => (state) => {
                // On rehydrate: if there's a persisted user, mark as "needs revalidation"
                // and keep the route guarded until the server confirms the session.
                if (state && state.user) {
                    state.isAuthenticated = false;
                    state.isRevalidating = true;
                }
            },
        }
    )
);

// Dashboard state store
interface DashboardState {
    selectedPolygonId: string | null;
    mapCenter: [number, number];
    mapZoom: number;
    sidebarOpen: boolean;
    sidebarCollapsed: boolean;
    viewMode: 'list' | 'map' | 'table';
    setSelectedPolygon: (id: string | null) => void;
    setMapView: (center: [number, number], zoom: number) => void;
    toggleSidebar: () => void;
    toggleSidebarCollapsed: () => void;
    setViewMode: (mode: 'list' | 'map' | 'table') => void;
}

const SIDEBAR_COLLAPSED_KEY = 'htl_sidebar_collapsed';

export const useDashboardStore = create<DashboardState>((set) => ({
    selectedPolygonId: null,
    mapCenter: [-1.8312, -78.1834], // Ecuador default
    mapZoom: 7,
    sidebarOpen: true,
    // Desktop rail state — persisted so the choice sticks across sessions.
    sidebarCollapsed:
        typeof localStorage !== 'undefined' &&
        localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
    viewMode: 'map',
    setSelectedPolygon: (id) => set({ selectedPolygonId: id }),
    setMapView: (center, zoom) => set({ mapCenter: center, mapZoom: zoom }),
    toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
    toggleSidebarCollapsed: () =>
        set((state) => {
            const next = !state.sidebarCollapsed;
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
            } catch {
                /* ignore */
            }
            return { sidebarCollapsed: next };
        }),
    setViewMode: (mode) => set({ viewMode: mode }),
}));
